import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { bankGet } from "../api/ApiRoutesFile";

export const bankApiSlice = createApi({
  reducerPath: "bankApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Banks"],
  endpoints: (builder) => ({
    banks: builder.query({
      query: ({ endpoint, id, params }) => {
        const url = id ? `${endpoint}/${id}` : endpoint;
        return {
          url,
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Banks",
                id: _id,
              })),
              { type: "Banks", id: "LIST" },
            ]
          : [{ type: "Banks", id: "LIST" }],
    }),

    createBank: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.data && data.data._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              bankApiSlice.util.updateQueryData(
                "banks",
                { endpoint: bankGet },
                (draft) => {
                  // Insert the new lot at the beginning (top)
                  draft.data.unshift(data.data);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateBank: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.data) {
            // Update paginated cache if applicable
            dispatch(
              bankApiSlice.util.updateQueryData(
                "banks",
                { endpoint: bankGet },
                (draft) => {
                  const index = draft.data.findIndex((a) => a._id === id);
                  if (index !== -1) {
                    draft.data[index] = data.data;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteBank: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}${id}`,
        method: "DELETE", // Use DELETE if your API supports it
      }),

      // Only update the cache after the API call succeeds
      async onQueryStarted({ endpoint, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
    
          if (data && data?.data) {
            dispatch(
              bankApiSlice.util.updateQueryData(
                "banks",
                { endpoint: bankGet }, // Adjust the ID or params if dynamic
                (draft) => {
                  if (draft && draft.data) {
                    draft.data = draft.data.filter((item) => item._id !== id);
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete failed:", error);
        }
      },
    }),
  }),
});

export const {
  useBanksQuery,
  useCreateBankMutation,
  useUpdateBankMutation,
  useDeleteBankMutation,
  useLazyBanksQuery,
} = bankApiSlice;
